// src/components/pages/admin-dash/AdminSubscriptions.jsx
import React, { useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";
import ConfirmDialog from "../../ui/ConfirmDialog";
import AdminTable from "./AdminTable";

/**
 * Admin view over subscriptions (SubscriptionAdminResource, /admin/subscription).
 *
 * Reuses the same backend logic the self-service /me and /cancel endpoints
 * use — SubscriptionService.getCurrentSubscription/cancelCurrentSubscription
 * already take a userId parameter, this just supplies someone else's.
 *
 * Backend endpoints:
 *   GET  /admin/subscription/list-by-filter?plan=&status=&paymentMethod=
 *   GET  /admin/subscription/list-by-user/{userId}
 *   POST /admin/subscription/cancel/{userId}
 */
const PLAN_OPTIONS = [
  { value: "",           label: "All plans"    },
  { value: "MONTHLY_30", label: "Monthly"      },
  { value: "PLUS",       label: "Plus"         },
  { value: "PRO",        label: "Pro"          },
  { value: "ELITE",      label: "Elite"        },
];

const STATUS_OPTIONS = [
  { value: "",         label: "All statuses" },
  { value: "PENDING",  label: "Pending"      },
  { value: "ACTIVE",   label: "Active"       },
  { value: "PAST_DUE", label: "Past due"     },
  { value: "CANCELED", label: "Canceled"     },
  { value: "EXPIRED",  label: "Expired"      },
  { value: "ERROR",    label: "Error"        },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: "",       label: "All methods" },
  { value: "STRIPE", label: "Stripe"      },
  { value: "PAYPAL", label: "PayPal"      },
];

const CANCELLABLE_STATUSES = ["PENDING", "ACTIVE", "PAST_DUE"];

const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
    : String(n ?? "—");

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

export default function AdminSubscriptions({ onShowToast }) {
  const [plan, setPlan]                   = useState("");
  const [status, setStatus]               = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState("");
  const [didSearch, setDidSearch]         = useState(false);
  const [selected, setSelected]           = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [canceling, setCanceling]         = useState(false);
  const [reprocessing, setReprocessing]   = useState(false);
  const [extending, setExtending]         = useState(false);
  const [changingPlan, setChangingPlan]   = useState(false);

  const handleSearch = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (plan) params.plan = plan;
      if (status) params.status = status;
      if (paymentMethod) params.paymentMethod = paymentMethod;
      const res = await axiosInstance.get("/admin/subscription/list-by-filter", { params });
      setSubscriptions(Array.isArray(res.data) ? res.data : []);
      setDidSearch(true);
    } catch {
      setError("Failed to load subscriptions.");
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPlan(""); setStatus(""); setPaymentMethod("");
    setSubscriptions([]); setDidSearch(false); setError("");
  };

  const handleCancel = async () => {
    if (!selected) return;
    setCanceling(true);
    try {
      await axiosInstance.post(`/admin/subscription/cancel/${selected.user.id}`);
      onShowToast?.(`Subscription for ${selected.user.name} canceled.`, "success");
      setConfirmCancel(false);
      setSelected(null);
      handleSearch();
    } catch {
      onShowToast?.("Couldn't cancel this subscription.", "error");
    } finally {
      setCanceling(false);
    }
  };

  const handleReprocess = async (providerTransactionId, note) => {
    if (!selected) return;
    setReprocessing(true);
    try {
      const res = await axiosInstance.post(
        `/admin/subscription/reprocess-payment/${selected.user.id}`,
        { providerTransactionId, note }
      );
      if (res?.data?.alreadyProcessed) {
        onShowToast?.("That payment was already processed — no credit granted.", "info");
      } else {
        onShowToast?.(
          `Credit granted for ${selected.user.name}. New balance: ${res?.data?.newCredit ?? "—"}.`,
          "success"
        );
      }
      setSelected(null);
      handleSearch();
    } catch (err) {
      onShowToast?.(err?.response?.data?.message || "Couldn't reprocess this payment.", "error");
    } finally {
      setReprocessing(false);
    }
  };

  const handleExtend = async (days, grantCredit, note) => {
    if (!selected) return;
    setExtending(true);
    try {
      const res = await axiosInstance.post(
        `/admin/subscription/extend/${selected.user.id}`,
        { days, grantCredit, note }
      );
      onShowToast?.(
        `${selected.user.name}'s subscription extended to ${fmtDate(res?.data?.currentPeriodEnd)}.`,
        "success"
      );
      setSelected(null);
      handleSearch();
    } catch (err) {
      onShowToast?.(err?.response?.data?.message || "Couldn't extend this subscription.", "error");
    } finally {
      setExtending(false);
    }
  };

  const handleChangePlan = async (newPlan, note) => {
    if (!selected) return;
    setChangingPlan(true);
    try {
      const res = await axiosInstance.post(
        `/admin/subscription/change-plan/${selected.user.id}`,
        { plan: newPlan, note }
      );
      if (res?.data?.requiresAction) {
        onShowToast?.(
          "PayPal plan change requires the customer's approval — they'll see it next time they log in.",
          "info"
        );
      } else {
        onShowToast?.(`${selected.user.name}'s plan changed to ${newPlan}.`, "success");
      }
      setSelected(null);
      handleSearch();
    } catch (err) {
      onShowToast?.(err?.response?.data?.message || "Couldn't change this subscription's plan.", "error");
    } finally {
      setChangingPlan(false);
    }
  };

  const columns = [
    { key: "id",     label: "ID",     sort: true, width: "70px",
      render: (s) => <span className="tabular-nums" style={{ color: "var(--txt-2)" }}>#{s.id}</span> },
    { key: "user",   label: "User",   sort: true, value: (s) => s.user?.name || "",
      render: (s) => (
        <div>
          <div style={{ color: "var(--txt)" }} className="font-medium">{s.user?.name || "—"}</div>
          <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>#{s.user?.id ?? "?"}</div>
        </div>
      ),
    },
    { key: "plan",   label: "Plan",   sort: true, value: (s) => s.plan || "",
      render: (s) => s.plan || "—" },
    { key: "status", label: "Status", sort: true, value: (s) => s.status || "",
      render: (s) => <SubscriptionStatusBadge status={s.status} /> },
    { key: "method", label: "Method", sort: true, value: (s) => s.paymentMethod || "",
      render: (s) => s.paymentMethod ? <Badge variant="neutral">{s.paymentMethod}</Badge> : "—" },
    { key: "amount", label: "Amount", sort: true, align: "right", value: (s) => s.amount ?? 0,
      render: (s) => <span className="tabular-nums font-medium">{fmtUsd(s.amount)}</span> },
    { key: "credit", label: "Credit/mo", sort: true, align: "right", value: (s) => s.monthlyCredit ?? 0,
      render: (s) => <span className="tabular-nums">{s.monthlyCredit ?? "—"}</span> },
    { key: "periodEnd", label: "Period end", sort: true, value: (s) => s.currentPeriodEnd || "",
      render: (s) => <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{fmtDate(s.currentPeriodEnd)}</span> },
    { key: "cancelRequested", label: "Cancel req.", align: "center", width: "100px",
      render: (s) => s.cancelRequested
        ? <Badge variant="gold">Yes</Badge>
        : <span style={{ color: "var(--txt-3)" }}>—</span> },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Subscriptions
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Search subscriptions by plan, status, or payment method. Click a row for details.
        </p>
      </header>

      <Card variant="soft" className="!p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
          <SelectField label="Plan"   value={plan}          onChange={setPlan}          options={PLAN_OPTIONS} />
          <SelectField label="Status" value={status}        onChange={setStatus}        options={STATUS_OPTIONS} />
          <SelectField label="Method" value={paymentMethod} onChange={setPaymentMethod} options={PAYMENT_METHOD_OPTIONS} />
          <div className="flex items-end gap-2">
            <Button icon="search" onClick={handleSearch} loading={loading} size="md">Search
            </Button>
            {(plan || status || paymentMethod || didSearch) && (
              <button
                type="button"
                onClick={handleReset}
                className="text-[12px] hover:underline px-1 pb-3"
                style={{ color: "var(--txt-2)" }}
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </Card>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{
            background: "var(--red-dim)",
            border: "1px solid var(--red-border)",
            color: "var(--red)",
          }}
        >
          {error}
        </div>
      )}

      <AdminTable
        title={didSearch ? `${subscriptions.length} results` : undefined}
        columns={columns}
        rows={subscriptions}
        rowKey="id"
        loading={loading}
        onRowClick={setSelected}
        emptyIcon="credit-card"
        emptyMessage={
          didSearch
            ? "No subscriptions match these filters."
            : "Pick filters above and search to begin."
        }
        defaultSort={{ key: "id", dir: "desc" }}
      />

      {selected && (
        <SubscriptionDetailModal
          subscription={selected}
          onClose={() => setSelected(null)}
          onCancel={() => setConfirmCancel(true)}
          onReprocess={handleReprocess}
          reprocessing={reprocessing}
          onExtend={handleExtend}
          extending={extending}
          onChangePlan={handleChangePlan}
          changingPlan={changingPlan}
        />
      )}

      {confirmCancel && (
        <ConfirmDialog
          open={confirmCancel}
          title="Cancel this subscription?"
          message={
            `${selected?.user?.name || "This user"}'s ${selected?.plan || ""} subscription will stop ` +
            `renewing. They keep access until the current period ends (${fmtDate(selected?.currentPeriodEnd)}) — ` +
            "this schedules cancellation with the provider, it doesn't cancel immediately."
          }
          confirmLabel="Cancel subscription"
          tone="danger"
          loading={canceling}
          onConfirm={handleCancel}
          onClose={() => setConfirmCancel(false)}
        />
      )}
    </div>
  );
}

const CHANGEABLE_PLANS = [
  { value: "MONTHLY_30", label: "Monthly" },
  { value: "PLUS",       label: "Plus"    },
  { value: "PRO",        label: "Pro"     },
  { value: "ELITE",      label: "Elite"   },
];

function SubscriptionDetailModal({
  subscription: s, onClose, onCancel,
  onReprocess, reprocessing,
  onExtend, extending,
  onChangePlan, changingPlan,
}) {
  const canCancel = CANCELLABLE_STATUSES.includes(s.status) && !s.cancelRequested;
  const canChangePlan = s.status === "ACTIVE" && !!s.providerSubscriptionId;

  // Only one inline action panel open at a time: null | "reprocess" | "extend" | "changePlan"
  const [activePanel, setActivePanel] = useState(null);
  const closePanel = () => setActivePanel(null);

  const [txnId, setTxnId] = useState("");
  const [reprocessNote, setReprocessNote] = useState("");
  const canSubmitReprocess = txnId.trim().length > 0 && reprocessNote.trim().length >= 4;

  const [days, setDays] = useState("30");
  const [grantCredit, setGrantCredit] = useState(true);
  const [extendNote, setExtendNote] = useState("");
  const daysNum = Number(days);
  const canSubmitExtend = Number.isFinite(daysNum) && daysNum > 0 && extendNote.trim().length >= 4;

  const [newPlan, setNewPlan] = useState(CHANGEABLE_PLANS.find((p) => p.value !== s.plan)?.value || "PLUS");
  const [changePlanNote, setChangePlanNote] = useState("");
  const canSubmitChangePlan = newPlan && newPlan !== s.plan && changePlanNote.trim().length >= 4;

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center px-4 py-8"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl fade-in-up max-h-[90vh] overflow-y-auto"
        style={{
          background: "var(--surf)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r-xl)",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        <div
          className="flex items-start justify-between gap-3 p-6"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <div>
            <h2 className="font-semibold leading-tight" style={{ fontSize: 20, color: "var(--txt)" }}>
              {s.user?.name || "—"}
              <span className="text-[13px] ml-2" style={{ color: "var(--txt-3)" }}>#{s.user?.id}</span>
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Badge variant="neutral">Subscription #{s.id}</Badge>
              <SubscriptionStatusBadge status={s.status} />
              {s.cancelRequested && <Badge variant="gold">Cancellation requested</Badge>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md transition-colors p-1.5"
            style={{ color: "var(--txt-2)" }}
            aria-label="Close"
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            <DetailField label="Plan" value={s.plan} />
            <DetailField label="Payment method" value={s.paymentMethod} />
            <DetailField label="Amount" value={fmtUsd(s.amount)} />
            <DetailField label="Monthly credit" value={s.monthlyCredit} mono />
            <DetailField label="Current period start" value={fmtDate(s.currentPeriodStart)} />
            <DetailField label="Current period end" value={fmtDate(s.currentPeriodEnd)} />
            <DetailField label="Last credit granted" value={fmtDate(s.lastCreditGrantedAt)} />
            <DetailField label="Canceled at" value={fmtDate(s.canceledAt)} />
            <DetailField label="Provider subscription ID" value={s.providerSubscriptionId} mono />
            <DetailField label="Provider plan ID" value={s.providerPlanId} mono />
          </div>

          {activePanel === "reprocess" && (
            <div
              className="rounded-xl p-4 space-y-3"
              style={{ background: "var(--gold-dim)", border: "1px solid var(--gold-border)" }}
            >
              <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
                Paste the Stripe invoice ID or PayPal sale ID for the payment that never made it
                through the webhook. It's verified against the provider before any credit is
                granted, and safe to submit twice — reprocessing an already-applied payment is a
                no-op.
              </p>
              <Field
                label="Provider transaction ID"
                value={txnId}
                onChange={(e) => setTxnId(e.target.value)}
                placeholder="e.g. in_1AbCdEfGh or 8XY123456A"
                className="!mb-0"
              />
              <Field
                label="Audit note"
                value={reprocessNote}
                onChange={(e) => setReprocessNote(e.target.value)}
                placeholder="Why this is being reprocessed"
                className="!mb-0"
              />
              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={closePanel} disabled={reprocessing}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  icon="refresh"
                  loading={reprocessing}
                  disabled={!canSubmitReprocess}
                  onClick={() => onReprocess(txnId.trim(), reprocessNote.trim())}
                >
                  Confirm reprocess
                </Button>
              </div>
            </div>
          )}

          {activePanel === "extend" && (
            <div
              className="rounded-xl p-4 space-y-3"
              style={{ background: "var(--gold-dim)", border: "1px solid var(--gold-border)" }}
            >
              <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
                Local-only courtesy extension — doesn't touch Stripe/PayPal, so it never conflicts
                with the provider's own billing cycle. Use for goodwill (e.g. a billing incident),
                not to fix a real missed payment — that's "Reprocess payment" instead.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label="Extend by (days)"
                  type="number"
                  min={1}
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className="!mb-0"
                />
                <label className="flex items-center gap-2 text-[12px] mt-6" style={{ color: "var(--txt)" }}>
                  <input
                    type="checkbox"
                    checked={grantCredit}
                    onChange={(e) => setGrantCredit(e.target.checked)}
                  />
                  Also grant {s.monthlyCredit ?? "the"} credit{s.monthlyCredit === 1 ? "" : "s"} for this period
                </label>
              </div>
              <Field
                label="Audit note"
                value={extendNote}
                onChange={(e) => setExtendNote(e.target.value)}
                placeholder="Why this courtesy extension is being granted"
                className="!mb-0"
              />
              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={closePanel} disabled={extending}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  icon="calendar-plus"
                  loading={extending}
                  disabled={!canSubmitExtend}
                  onClick={() => onExtend(daysNum, grantCredit, extendNote.trim())}
                >
                  Confirm extension
                </Button>
              </div>
            </div>
          )}

          {activePanel === "changePlan" && (
            <div
              className="rounded-xl p-4 space-y-3"
              style={{ background: "var(--gold-dim)", border: "1px solid var(--gold-border)" }}
            >
              <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
                Changes the tier on the user's behalf via the real Stripe/PayPal API — prorated on
                Stripe; PayPal requires the customer's approval on next login.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <SelectField
                  label="New plan"
                  value={newPlan}
                  onChange={setNewPlan}
                  options={CHANGEABLE_PLANS.filter((p) => p.value !== s.plan)}
                />
              </div>
              <Field
                label="Audit note"
                value={changePlanNote}
                onChange={(e) => setChangePlanNote(e.target.value)}
                placeholder="Why this plan change is being made"
                className="!mb-0"
              />
              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={closePanel} disabled={changingPlan}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  icon="repeat"
                  loading={changingPlan}
                  disabled={!canSubmitChangePlan}
                  onClick={() => onChangePlan(newPlan, changePlanNote.trim())}
                >
                  Confirm plan change
                </Button>
              </div>
            </div>
          )}
        </div>

        <div
          className="flex flex-wrap items-center justify-end gap-2 px-6 py-4"
          style={{ borderTop: "1px solid var(--border)" }}
        >
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          {!activePanel && (
            <>
              {(s.paymentMethod === "STRIPE" || s.paymentMethod === "PAYPAL") && (
                <Button variant="ghost" size="sm" icon="refresh" onClick={() => setActivePanel("reprocess")}>
                  Reprocess payment
                </Button>
              )}
              <Button variant="ghost" size="sm" icon="calendar-plus" onClick={() => setActivePanel("extend")}>
                Extend period
              </Button>
              {canChangePlan && (
                <Button variant="ghost" size="sm" icon="repeat" onClick={() => setActivePanel("changePlan")}>
                  Change plan
                </Button>
              )}
            </>
          )}
          {canCancel && (
            <Button variant="danger" size="sm" icon="ban" onClick={onCancel}>
              Cancel subscription
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value, mono }) {
  return (
    <div>
      <div className="lg-label mb-1">{label}</div>
      <div
        className={`text-[13.5px] ${mono ? "tabular-nums" : ""}`}
        style={{ color: "var(--txt)", wordBreak: "break-word" }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label
        className="block text-[12px] font-medium mb-1.5 tracking-wide"
        style={{ color: "var(--txt)" }}
      >
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3.5 py-[11px] text-[14px] outline-none rounded-full border appearance-none cursor-pointer pr-9"
          style={{
            background: "var(--surf-2)",
            color: "var(--txt)",
            borderColor: "var(--border-2)",
          }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span
          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px]"
          style={{ color: "var(--txt-3)" }}
        >
          ▾
        </span>
      </div>
    </div>
  );
}

export function SubscriptionStatusBadge({ status }) {
  switch (status) {
    case "ACTIVE":   return <Badge variant="teal">Active</Badge>;
    case "PENDING":  return <Badge variant="gold">Pending</Badge>;
    case "PAST_DUE": return <Badge variant="red">Past due</Badge>;
    case "CANCELED": return <Badge variant="neutral">Canceled</Badge>;
    case "EXPIRED":  return <Badge variant="neutral">Expired</Badge>;
    case "ERROR":    return <Badge variant="red">Error</Badge>;
    default:         return <Badge variant="neutral">{status || "—"}</Badge>;
  }
}
